import { useMutation } from '@apollo/client';
import DefaultModal from 'components/common/DefaultModal';
import { ERRORS } from 'constants/notify';
import useCurrentLoggedInUser from 'hooks/apollo/queries/useCurrentLoggedInUser';
import React, { useCallback, useMemo, useState } from 'react';
import SweetAlert from 'react-bootstrap-sweetalert';
import { useDispatch } from 'react-redux';
import SelectedAppointmentContactAction from 'redux/selected-contact/selected-contact.action';
import { ISelectedAppointmentContactState } from 'redux/selected-contact/types';
import NotaryTable from 'routes/Transaction/components/TransactionDetailTab/TransactionForm/NotaryTableModal/NotaryTable';
import { UPDATE_APPOINTMENT_ASSIGNMENT } from 'services/graphql/mutations/appointmentAssignments';
import { GENERATE_APPOINTMENT_SHIPPING_LABEL, UPDATE_APPOINTMENT } from 'services/graphql/mutations/appointments';
import { AssignmentStatus, IAssignment } from 'types/appointmentAssignments';
import { AppointmentNotaryTypeDisplay, AppointmentStatus, DocumentStatus, IAppointment } from 'types/appointments';
import { GeneralFunction } from 'types/common';
import { IDocument } from 'types/file';
import { ITransactionType } from 'types/services';
import { defaultNotification, getEncryptedFilesLinks } from 'utils/utils';

import CardActionButton from '../AppointmentsTabs/CardActionButton';
import AppointmentDetail from './AppointmentDetail';
import CancelAppointmentForm from './CancelAppointmentForm';
import UploadDocumentForm from './UploadDocumentForm';

type ModalType =
  | 'detail'
  | 'cancel'
  | 'complete-appointment'
  | 'upload'
  | 'upload-complete'
  | 'mail-complete'
  | 'notary';

export const useCommonActionButtonsForAppointments = ({
  appointment,
  refetch,
  transactionTypesData,
}: {
  appointment: IAppointment;
  refetch?: GeneralFunction;
  transactionTypesData?: ITransactionType[];
}) => {
  const userId = useCurrentLoggedInUser().id;
  const [modalType, setModalType] = useState<ModalType>();
  const [updateAssignment] = useMutation(UPDATE_APPOINTMENT_ASSIGNMENT);
  const [updateAppointment] = useMutation(UPDATE_APPOINTMENT);
  const dispatch = useDispatch();
  const closeModal = () => {
    setModalType(undefined);
  };

  const mutationComplete = (error?: any) => {
    if (!error) {
      defaultNotification({ success: 'Request changed successfully!' });
      refetch?.();
    } else {
      console.error('Error', error);
      defaultNotification({ success: 'Something went wrong! Please try again' });
    }
  };

  const mutateAssignment = async (assignmentStatus: AssignmentStatus, assignment?: IAssignment) => {
    if (!assignment) return;
    try {
      await updateAssignment({
        variables: {
          filter: {
            id: assignment.id,
          },
          updatedDoc: {
            status: assignmentStatus,
          },
        },
      });
      mutationComplete();
    } catch (err) {
      mutationComplete(err);
    }
  };

  const mutateAppointment = async (appointmentParam: IAppointment, updatedDocs?: Record<string, any>) => {
    try {
      await updateAppointment({
        variables: {
          filter: {
            id: appointmentParam.id,
          },
          updatedDoc: updatedDocs,
        },
      });
      defaultNotification({ success: 'Update Appointment Status successfully!' });
      refetch?.();
    } catch (err) {
      defaultNotification({ failure: ERRORS.DEFAULT_MUTATION_ERROR });
    } finally {
      closeModal();
    }
  };

  const assignNotaryClick = () => {
    setModalType('notary');
  };

  const acceptAppointmentClick = (assignment?: IAssignment) => {
    mutateAssignment(AssignmentStatus.APPROVED, assignment);
  };

  const rejectAppointmentClick = (assignment?: IAssignment) => {
    mutateAssignment(AssignmentStatus.REJECTED, assignment);
  };

  const cancelAppointmentClick = () => {
    setModalType('cancel');
  };

  const completeAppointmentClick = () => {
    setModalType('complete-appointment');
  };

  const appointmentCardClick = () => {
    setModalType('detail');
  };

  const markAppointmentMailedClick = () => {
    setModalType('mail-complete');
  };
  const markAppointmentUploadedClick = () => {
    setModalType('upload-complete');
  };
  const uploadDocumentClick = () => {
    setModalType('upload');
  };

  const markAppointmentAsCompleted = (appointmentParam?: IAppointment) => {
    mutateAppointment(appointmentParam || appointment, { status: AppointmentStatus.COMPLETED });
  };

  const confirmMarkAppointmentMailed = (appointmentParam?: IAppointment) => {
    mutateAppointment(appointmentParam || appointment, { documentStatus: DocumentStatus.MAILED });
  };

  const confirmMarkAppointmentUploaded = (appointmentParam?: IAppointment) => {
    mutateAppointment(appointmentParam || appointment, { documentStatus: DocumentStatus.SCANNED });
  };

  const [generateShippingLabel, { loading: generateShippingLabelLoading }] = useMutation(
    GENERATE_APPOINTMENT_SHIPPING_LABEL,
  );

  const getShippingLabel = async (appointmentParam?: IAppointment) => {
    const appointmentMarked = appointmentParam || appointment;

    if (appointmentMarked.labelFiles?.length) {
      const file = appointmentMarked.labelFiles[0];
      const { viewUrl } = await getEncryptedFilesLinks(file.id, file.name);
      window.open(viewUrl, '_blank');
      refetch?.();
      return;
    }
    try {
      const response = await generateShippingLabel({
        variables: { appointmentId: appointmentMarked.id },
      });
      const document: IDocument = response.data.generateShippingLabel;

      if (document?.files?.length) {
        const file = document?.files?.[0] || {};
        const { viewUrl } = await getEncryptedFilesLinks(file.id, file.name);
        window.open(viewUrl, '_blank');
      }
      refetch?.();
    } catch (err) {
      const error = JSON.parse(JSON.stringify(err));
      console.log(error);

      if (error?.errors?.code === 'RECIPIENTS.ADDRESSSTATEORPROVINCECODE.MISMATCH') {
        defaultNotification({ failure: 'Recipient state and postal code mismatch!' });
      }
      defaultNotification({ failure: ERRORS.DEFAULT_MUTATION_ERROR });
    }
  };

  const contactClick = (appointmentParam?: IAppointment) => {
    const appointmentMarked = appointmentParam || appointment;
    const contactInfo: Partial<ISelectedAppointmentContactState> = {
      description: appointmentMarked.description,
      id: appointmentMarked.chatRoom?.id || '',
      active: true,
      date: appointmentMarked.date,
      chatRoom: appointmentMarked.chatRoom,
    };
    dispatch(SelectedAppointmentContactAction.setSelectedAppointmentContact(contactInfo));
  };

  const modalTypeProps = ['notary', 'detail', 'cancel', 'upload'];

  const modalProps = useMemo(() => {
    const tempBaseProps = {
      onCancel: closeModal,
      visible: !!modalType && modalTypeProps.includes(modalType),
    };
    switch (modalType) {
      case 'notary':
        return {
          ...tempBaseProps,
          width: 850,
          style: { maxWidth: '100%', padding: 0 },
          bodyStyle: { padding: '16px 24px' },
          title: 'Add Notary',
          destroyOnClose: true,
          onCancel: closeModal,
          maskClosable: false,
        };
      case 'detail':
        return {
          ...tempBaseProps,
          title: `Appointment Detail - ${AppointmentNotaryTypeDisplay[appointment.type]}`,
          onCancel: closeModal,
        };
      case 'cancel':
        return {
          ...tempBaseProps,
          title: `Cancel Appointment`,
        };
      case 'upload':
        return {
          ...tempBaseProps,
          title: `Upload Signed Documents`,
        };
      default:
        return tempBaseProps;
    }
  }, [modalType]);

  const renderModalContent = useCallback(() => {
    const ownedAssignment = appointment.assignments?.find((assignment) => assignment.notary.id === userId);
    let ContentComponent: any;
    switch (modalType) {
      case 'notary':
        ContentComponent = (
          <NotaryTable
            callbacks={{ callbackOnSuccess: closeModal }}
            selectedAppointment={appointment}
            refetchTransactionData={refetch}
          />
        );
        break;
      case 'detail':
        ContentComponent = <AppointmentDetail appointment={appointment} transactionTypesData={transactionTypesData} />;
        break;
      case 'cancel':
        ContentComponent = <CancelAppointmentForm assignment={ownedAssignment} />;
        break;
      case 'upload':
        ContentComponent = (
          <UploadDocumentForm
            appointment={appointment}
            transaction={appointment.transaction}
            closeModal={closeModal}
            refetch={refetch}
          />
        );
        break;
      default:
        break;
    }
    return ContentComponent;
  }, [modalType, appointment, userId]);

  const AppointmentActionButtons = useCallback(
    () => (
      <CardActionButton
        appointment={appointment}
        acceptAppointmentClick={acceptAppointmentClick}
        rejectAppointmentClick={rejectAppointmentClick}
        cancelAppointmentClick={cancelAppointmentClick}
        completeAppointmentClick={completeAppointmentClick}
        markAppointmentUploadedClick={markAppointmentUploadedClick}
        markAppointmentMailedClick={markAppointmentMailedClick}
        uploadDocumentClick={uploadDocumentClick}
        assignNotaryClick={assignNotaryClick}
      />
    ),
    [appointment, generateShippingLabelLoading],
  );

  return {
    modalType,
    Modal: (
      <>
        <SweetAlert
          show={modalType === 'complete-appointment'}
          warning
          showCancel
          confirmBtnText='Mark Completed'
          confirmBtnBsStyle='info'
          cancelBtnText='Cancel'
          title='Mark this Appointment as Completed?'
          onConfirm={() => {
            markAppointmentAsCompleted(appointment);
          }}
          onCancel={closeModal}
          focusConfirmBtn
        />
        <SweetAlert
          show={modalType === 'upload-complete'}
          warning
          showCancel
          confirmBtnText='Mark Scanned'
          confirmBtnBsStyle='info'
          cancelBtnText='Cancel'
          title='Mark this Appointment as Documents Scanned?'
          onConfirm={() => {
            confirmMarkAppointmentUploaded(appointment);
          }}
          onCancel={closeModal}
          focusConfirmBtn
        />
        <SweetAlert
          show={modalType === 'mail-complete'}
          warning
          showCancel
          confirmBtnText='Mark Mailed'
          confirmBtnBsStyle='info'
          cancelBtnText='Cancel'
          title='Mark this Appointment as Documents Mailed?'
          onConfirm={() => {
            confirmMarkAppointmentMailed(appointment);
          }}
          onCancel={closeModal}
          focusConfirmBtn
        />
        <DefaultModal props={modalProps}>{renderModalContent()}</DefaultModal>
      </>
    ),
    ModalContent: renderModalContent,
    closeModal,
    contactClick,
    shippingLabelClick: getShippingLabel,
    cancelAppointmentClick,
    acceptAppointmentClick,
    rejectAppointmentClick,
    appointmentCardClick,
    completeAppointmentClick,
    markAppointmentAsCompleted,
    markAppointmentUploadedClick,
    markAppointmentMailedClick,
    uploadDocumentClick,
    confirmMarkAppointmentMailed,
    confirmMarkAppointmentUploaded,
    getShippingLabel,
    AppointmentActionButtons,
    assignNotaryClick,
    generateShippingLabelLoading,
  };
};
